import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FiChevronDown, FiGift, FiLogOut, FiMenu, FiPackage, FiPrinter, FiTag, FiUser, FiX } from "react-icons/fi";
import { usePrintFlow } from "../print/state/PrintFlowContext";
import { auth } from "../print/api/client";
import { CTA_LAYOUT_ID, CTA_TRANSITION, measureCta, useCtaInNav } from "./ctaStore";

const PUBLIC_LINKS = [
  { to: "/#how-it-works", label: "How it works" },
  { to: "/#features", label: "Features" },
  { to: "/#partner", label: "Partner with us" },
];

export default function Navbar() {
  const { session } = usePrintFlow();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);
  const ctaInNav = useCtaInNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMenu(false);
    measureCta();
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menu]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const transparent = !scrolled && !open;
  const name = session.profile && session.profile.fullName ? session.profile.fullName.split(" ")[0] : session.mobile ? `+91 ${session.mobile}` : "Account";
  const initials = session.profile && session.profile.fullName ? session.profile.fullName.trim().charAt(0).toUpperCase() : session.mobile ? session.mobile.slice(-2) : "";

  const logout = () => {
    auth.clear();
    setMenu(false);
    navigate("/");
  };

  const goHash = (e, to) => {
    if (!to.startsWith("/#")) return;
    const id = to.slice(2);
    if (location.pathname === "/") {
      e.preventDefault();
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header className={`nav ${transparent ? "nav--transparent" : "nav--solid"}`} id="menu">
      <div className="nav__inner">
        <Link to="/" className="nav__brand" aria-label="PrintAnywhere home">
          <img src="/img/print_anywhere_logo2.webp" alt="" />
          <span>
            <b>Print</b>Anywhere
          </span>
        </Link>

        <nav className="nav__links" aria-label="Primary">
          {PUBLIC_LINKS.map((l) =>
            l.to.startsWith("/#") ? (
              <a key={l.to} href={l.to} className="nav__link" onClick={(e) => goHash(e, l.to)}>
                {l.label}
              </a>
            ) : (
              <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `nav__link ${isActive ? "is-active" : ""}`}>
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="nav__actions">
          {ctaInNav ? (
            <motion.div className="nav__cta-slot" layoutId={CTA_LAYOUT_ID} transition={CTA_TRANSITION}>
              <Link to="/print" className={`nav__cta ${location.pathname.startsWith("/print") ? "is-active" : ""}`}>
                <FiPrinter /> <span>Print now</span>
              </Link>
            </motion.div>
          ) : null}
          {session.isLoggedIn ? (
            <div className="nav__account" ref={menuRef}>
              <button type="button" className="nav__avatar" onClick={() => setMenu((m) => !m)} aria-haspopup="menu" aria-expanded={menu}>
                <span className="nav__initials">{initials || <FiUser />}</span>
                <span className="nav__name">{name}</span>
                <FiChevronDown />
              </button>
              <AnimatePresence>
                {menu ? (
                  <motion.div className="nav__menu" role="menu" initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: 0.98 }} transition={{ duration: 0.16 }}>
                    <div className="nav__menu-head">
                      <strong>{session.profile && session.profile.fullName ? session.profile.fullName : "PrintAnywhere user"}</strong>
                      <span>+91 {session.mobile}</span>
                    </div>
                    <Link to="/orders" className="nav__menu-item" role="menuitem">
                      <FiPackage /> My orders
                    </Link>
                    <Link to="/refer" className="nav__menu-item" role="menuitem">
                      <FiGift /> Refer &amp; Earn
                    </Link>
                    <Link to="/offers" className="nav__menu-item" role="menuitem">
                      <FiTag /> Offers
                    </Link>
                    <Link to="/account" className="nav__menu-item" role="menuitem">
                      <FiUser /> Account
                    </Link>
                    <button type="button" className="nav__menu-item nav__menu-item--danger" onClick={logout} role="menuitem">
                      <FiLogOut /> Log out
                    </button>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          ) : (
            <Link to="/login" className="nav__login">
              Log in
            </Link>
          )}
          <button type="button" className="nav__burger" onClick={() => setOpen((o) => !o)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}>
            {open ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open ? (
          <motion.div className="nav__drawer" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
            {PUBLIC_LINKS.map((l) =>
              l.to.startsWith("/#") ? (
                <a key={l.to} href={l.to} className="nav__drawer-link" onClick={(e) => { goHash(e, l.to); setOpen(false); }}>
                  {l.label}
                </a>
              ) : (
                <Link key={l.to} to={l.to} className="nav__drawer-link">
                  {l.label}
                </Link>
              )
            )}
            <div className="nav__drawer-sep" />
            {session.isLoggedIn ? (
              <>
                <Link to="/orders" className="nav__drawer-link"><FiPackage /> My orders</Link>
                <Link to="/refer" className="nav__drawer-link"><FiGift /> Refer &amp; Earn</Link>
                <Link to="/offers" className="nav__drawer-link"><FiTag /> Offers</Link>
                <Link to="/account" className="nav__drawer-link"><FiUser /> Account</Link>
                <button type="button" className="nav__drawer-link nav__drawer-link--danger" onClick={logout}><FiLogOut /> Log out</button>
              </>
            ) : (
              <Link to="/login" className="nav__drawer-link"><FiUser /> Log in</Link>
            )}
            <Link to="/print" className="pa-btn pa-btn--primary pa-btn--lg pa-btn--block nav__drawer-cta">
              <FiPrinter /> <span>Print now</span>
            </Link>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}
